import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'

import SkeletonField from './SkeletonField'

export type SkeletonAgreementGeneralProps = {
    works?: number
    advances?: number
    tags?: number
}

const SectionHeading = ({ width }: { width: number }) => (
    <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1, mb: 2 }}>
        <Skeleton variant="text" width={width} sx={{ fontSize: '1.25rem' }} />
    </Box>
)

const WorkBlock = () => (
    <Stack spacing={1} sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <Skeleton variant="circular" width={20} height={20} />
            <Stack spacing={0.5} sx={{ flexGrow: 1 }}>
                <Skeleton variant="text" width={200} sx={{ fontSize: '0.75rem' }} />
                <Skeleton variant="text" width={140} />
            </Stack>
        </Stack>
        <Grid container spacing={2} sx={{ pl: 4 }}>
            {Array.from({ length: 4 }, (_, index) => (
                <Grid key={index} size={3}>
                    <Skeleton variant="text" width="80%" sx={{ fontSize: '0.75rem' }} />
                    <Skeleton variant="text" width="65%" />
                </Grid>
            ))}
        </Grid>
    </Stack>
)

export default function SkeletonAgreementGeneral(props: Readonly<SkeletonAgreementGeneralProps>) {
    const { works = 3, advances = 1, tags = 4 } = props

    return (
        <Grid container spacing={2} size={12}>
            <Grid size={12}>
                <Paper sx={{ p: 2 }}>
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
                    >
                        <Skeleton variant="text" width={160} sx={{ fontSize: '1.5rem' }} />
                        <Skeleton variant="circular" width={20} height={20} />
                    </Stack>
                </Paper>
            </Grid>

            <Grid size={12}>
                <Paper sx={{ p: 3 }}>
                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12, md: 7 }}>
                            <SectionHeading width={90} />
                            {Array.from({ length: works }, (_, index) => (
                                <WorkBlock key={index} />
                            ))}
                            <SkeletonField labelWidth="25%" valueWidth="70%" />
                        </Grid>

                        <Grid size={{ xs: 12, md: 5 }}>
                            <SectionHeading width={120} />
                            <Grid container spacing={3} rowSpacing={3}>
                                {Array.from({ length: 6 }, (_, index) => (
                                    <Grid key={index} size={{ xs: 12, sm: 4 }}>
                                        <SkeletonField />
                                    </Grid>
                                ))}
                                <Grid size={{ xs: 12, sm: 8 }}>
                                    <SkeletonField />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid size={12}>
                            <SectionHeading width={70} />
                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                                {Array.from({ length: tags }, (_, index) => (
                                    <Skeleton
                                        key={index}
                                        variant="rounded"
                                        width={110 + ((index * 29) % 60)}
                                        height={28}
                                        sx={{ borderRadius: 4 }}
                                    />
                                ))}
                            </Stack>
                        </Grid>

                        <Grid size={{ xs: 12, md: 7 }}>
                            <SectionHeading width={110} />
                            <Stack
                                direction="row"
                                spacing={2}
                                sx={{
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 2,
                                }}
                            >
                                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                                    <Skeleton variant="circular" width={20} height={20} />
                                    <Skeleton variant="text" width={120} />
                                </Stack>
                                <Skeleton variant="rounded" width={140} height={40} />
                            </Stack>
                            <SkeletonField labelWidth="30%" valueWidth="60%" />
                        </Grid>

                        <Grid size={{ xs: 12, md: 5 }}>
                            <SectionHeading width={70} />
                            <Skeleton variant="text" width="85%" />
                        </Grid>

                        <Grid size={12}>
                            <SectionHeading width={100} />
                            <Stack spacing={2}>
                                {Array.from({ length: advances }, (_, index) => (
                                    <Box key={index} sx={{ bgcolor: 'action.hover', p: 2 }}>
                                        <Grid container spacing={3} rowSpacing={2}>
                                            {Array.from({ length: 4 }, (_, cell) => (
                                                <Grid key={cell} size={{ xs: 6, md: 3 }}>
                                                    <Skeleton
                                                        variant="text"
                                                        width="70%"
                                                        sx={{ fontSize: '0.75rem' }}
                                                    />
                                                    <Skeleton variant="text" width="50%" />
                                                </Grid>
                                            ))}
                                            <Grid size={12}>
                                                <Skeleton
                                                    variant="text"
                                                    width={60}
                                                    sx={{ fontSize: '0.75rem' }}
                                                />
                                                <Skeleton variant="text" width={40} />
                                            </Grid>
                                            {Array.from({ length: 3 }, (_, cell) => (
                                                <Grid key={cell} size={{ xs: 6, md: 4 }}>
                                                    <Skeleton
                                                        variant="text"
                                                        width="60%"
                                                        sx={{ fontSize: '0.75rem' }}
                                                    />
                                                    <Skeleton variant="text" width="75%" />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                ))}
                            </Stack>
                        </Grid>
                    </Grid>

                    <Stack direction="row" spacing={2} sx={{ justifyContent: 'center', pt: 4 }}>
                        <Skeleton variant="rounded" width={280} height={40} />
                        <Skeleton variant="rounded" width={280} height={40} />
                    </Stack>
                </Paper>
            </Grid>
        </Grid>
    )
}
